import { berlinDatumLang } from "@dj/domain/gemeinsam";
import { useState } from "react";
import { Abweisung } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";

/**
 * Einladungen zu gemeinsamen Drückjagden in Meine Drückjagden (oberflaeche.md §7, V-24; lesemodelle.md §6;
 * gemeinsame_drueckjagd/01, F-200): `region` „Einladungen“ vor der Liste, je Drückjagd und eingeladenem eigenen Revier
 * ein `article` „<Bezeichnung>“ mit Veranstalter, Jagddatum und dem eigenen Revier, dazu `button` „Annehmen“
 * (`BeteiligungAnnehmen`) und `button` „Ablehnen“ (`BeteiligungAblehnen`). Nach der Antwort verschwindet die Einladung;
 * angenommen steht die Drückjagd danach in der Liste. Ohne Einladung steht nichts da.
 */
type Antwort = "BeteiligungAnnehmen" | "BeteiligungAblehnen";

function datum(w: unknown): string {
  if (w instanceof Date) return berlinDatumLang(w);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(w ?? ""));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

export function Beteiligungseinladungen() {
  const { uid } = useSitzung();
  const einladungen = useSammlung(uid ? `personen/${uid}/drueckjagd_einladungen` : null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  if (einladungen.daten.length === 0) return null;
  const kennung = (z: Zeile, k: "Drückjagd" | "Revier") => (z["_kennungen"] as Record<string, string> | undefined)?.[k] ?? null;

  async function antworten(z: Zeile, name: Antwort) {
    setGrund(null);
    setLaeuft(z._id);
    const a = await befehl(name, { "Drückjagd": kennung(z, "Drückjagd"), Revier: kennung(z, "Revier") });
    setLaeuft(null);
    if (!a.ok) setGrund(abweisungsgrund(a));
  }

  const liste = [...einladungen.daten].sort((a, b) => String(a["Jagddatum"] ?? "").localeCompare(String(b["Jagddatum"] ?? "")));
  return (
    <section className="liste-bereich" role="region" aria-label="Einladungen">
      <h2 className="kopf"><Symbol name="einladung" />Einladungen</h2>
      {liste.map((z) => {
        const titel = `dj-einladung-${z._id}`;
        return (
          <article key={z._id} className="eintrag eintrag--knoepfe platte" aria-labelledby={titel}>
            <h3 className="eintrag__t" id={titel}>{String(z["Drückjagd"] ?? "Drückjagd")}</h3>
            <span className="eintrag__meta">{[z["Veranstalter"], datum(z["Jagddatum"]), z["Revier"] ? `für ${String(z["Revier"])}` : null].filter(Boolean).join(" · ")}</span>
            {laeuft !== z._id ? (
              <div className="raster raster--knoepfe">
                <Kachel symbol="haken" begriff="Annehmen" art="kompakt" onPress={() => void antworten(z, "BeteiligungAnnehmen")} />
                <Kachel symbol="schliessen" begriff="Ablehnen" art="kompakt" onPress={() => void antworten(z, "BeteiligungAblehnen")} />
              </div>
            ) : null}
          </article>
        );
      })}
      <Abweisung grund={grund} />
    </section>
  );
}
